import { isBlockedEmail } from '@commissionsight/shared';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../api.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { OAuthButtons } from '../components/OAuthButtons.js';
import { Button, Field, inputClass } from '../components/ui.js';
import { register } from '../lib/auth.js';
import { captureReferralCode, clearReferralCode } from '../lib/referral.js';

export function Register() {
  const { baseUrl } = useAuth();
  const navigate = useNavigate();
  // Prefill from an invite link (?email=…) so a invited lead lands ready to go.
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get('email')?.trim().toLowerCase() ?? '',
  );
  // Referral attribution (?ref=CODE), captured once and persisted so it survives
  // navigation; cleared on a successful signup.
  const [referralCode] = useState(captureReferralCode);
  const [accountName, setAccountName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Invisible anti-bot signals: when the form rendered + a honeypot field.
  const formStartedAt = useRef(Date.now());
  const [website, setWebsite] = useState('');

  // Work email only — give immediate feedback before hitting the server (which
  // enforces the same rule authoritatively).
  const personalEmail = email.includes('@') && isBlockedEmail(email);

  const submit = async () => {
    if (personalEmail) {
      setError(
        'Please register with your work email — personal addresses like Gmail or Yahoo aren’t accepted.',
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await register(
        baseUrl,
        email,
        accountName,
        { acceptedTerms: true },
        { website, formStartedAt: formStartedAt.current },
        referralCode,
      );
      clearReferralCode();
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout title="You're on the list">
        <div className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
            <span className="font-medium">{accountName}</span> has been created and is{' '}
            <span className="font-medium">pending approval</span>. We'll email{' '}
            <span className="font-medium">{email}</span> the moment access is granted.
          </div>
          <Button className="w-full" onClick={() => navigate('/login')}>
            Back to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start unifying your carrier statements in minutes."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-emerald-600 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {error && (
        <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      )}
      <OAuthButtons verb="Sign up" />
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (email && accountName && agreed) submit();
        }}
      >
        {/* Honeypot: hidden from humans; bots fill it and get silently dropped. */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
        />
        <Field label="Account name">
          <input
            className={inputClass}
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="Acme Brokerage"
          />
        </Field>
        <Field label="Work email">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            aria-invalid={personalEmail}
          />
          {personalEmail && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              Use your work email — Gmail, Yahoo, and other personal addresses aren’t accepted.
            </p>
          )}
        </Field>
        <label className="flex items-start gap-2.5 text-sm text-neutral-600 dark:text-neutral-300">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500/30 dark:border-neutral-600 dark:bg-neutral-900"
          />
          <span>
            I agree to the{' '}
            <a
              href="https://commissionsight.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-emerald-600 hover:underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://commissionsight.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-emerald-600 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
        <Button
          type="submit"
          className="w-full"
          loading={busy}
          disabled={!email || !accountName || !agreed || personalEmail}
        >
          {busy ? 'Creating…' : 'Create account'}
        </Button>
        <p className="text-center text-xs text-neutral-400">
          New accounts are reviewed before activation. You'll get an email when you're approved.
        </p>
      </form>
    </AuthLayout>
  );
}
